import { sum, words } from 'lodash';

/**
 * @fileoverview Password rule and expression evaluation utilities.
 * Used for parsing maxlength from rule strings and evaluating group/expression rules (e.g. upgrade-password).
 */

/**
 * Extracts the maxlength value from a rule string (e.g. "minlength: 8; maxlength: 12; ...").
 * @param {string} s - Rule string containing a maxlength clause.
 * @returns {string|undefined} The maxlength value or undefined if not found.
 */
const getMaxLength = s => {
	let postMaxStr = s.split('maxlength')[1];
	return (
		postMaxStr &&
		postMaxStr.substring(postMaxStr.indexOf(':') + 1, postMaxStr.indexOf(';')).trim()
	);
};
export default {
	getMaxLength
};
/**
 * Evaluates a numeric expression string (e.g. "1 1 >= 2", "1 1 == 2", "1 1 > 1").
 * @param {string} expression - Expression with tokens and operator (>=, ==, ===, or >).
 * @returns {boolean} Result of the comparison.
 */
const evaluateExpression = expression => {
	let tokens = expression.split(/\W+/).map(exp => parseInt(exp));
	let match = tokens.pop();
	if (expression.includes('>=')) {
		return sum(tokens) >= match;
	} else if (expression.includes('==') || expression.includes('===')) {
		return sum(tokens) == match;
	} else {
		return sum(tokens) > match;
	}
};

/**
 * Evaluates a group rule expression (e.g. "ruleA && ruleB >= 2") against completed rule state.
 * @param {object} group - Object with exp (expression string) and rule (array of { ruleId, completed }).
 * @returns {boolean} True if the group passes or has no rule/expression.
 */
export const verifyGroupExpression = group => {
	if (!group || !group.rule) {
		return true;
	}
	let expression = group.exp;
	const rules = group.rule;
	if (expression && expression.length > 0) {
		let tokens = words(expression, /[A-z](?:_?[A-z0-9]+)/g);
		tokens = tokens.map(token => {
			const rule = rules.filter(item => item.ruleId === token);
			let isCompleted = null;
			rule.length > 0 ? (isCompleted = rule[0].completed) : (isCompleted = 0);
			expression = expression.replace(token, isCompleted);
		});
		return evaluateExpression(expression.trim());
	} else {
		return true;
	}
};
/**
 * Builds a human-readable description for an expression (e.g. "Include atleast 2 of the following:").
 * @param {string} exp - Expression string containing >= or >.
 * @param {*} tokens - Unused; kept for API compatibility.
 * @returns {string} Description string.
 */
export const getDescription = (exp, tokens) => {
	if (exp.includes('>=')) {
		return `Include atleast ${exp[exp.length - 1]} of the following:`;
	} else {
		return `Include atleast ${parseInt(exp[exp.length - 1]) + 1} of the following:`;
	}
};

/**
 * Verifies user-details expression against completed rules. Used to gate password/flow completion.
 * @param {object} userDetails - Object with expression string (e.g. "ruleA ruleB >= 1").
 * @param {Array<{ruleId: string, completed: *}>} rules - Rule definitions with completion state.
 * @returns {boolean} True if expression is satisfied or empty; false otherwise.
 */
export const verifyExpression = (userDetails, rules) => {
	let expression = userDetails?.expression;
	if (expression?.length > 0) {
		let tokens = words(expression, /[A-z](?:_?[A-z0-9]+)/g);
		tokens = tokens.map(token => {
			const rule = rules.filter(item => item.ruleId === token);
			let isCompleted = null;
			rule.length > 0 ? (isCompleted = rule[0].completed) : (isCompleted = 0);
			expression = expression.replace(token, isCompleted);
		});
		return !evaluateExpressionOld(expression);
	} else {
		return true;
	}
};

/**
 * Legacy expression evaluator: parses tokens and checks if sum equals match (e.g. "1 1 == 2").
 * @param {string} expression - Space-separated expression with == (e.g. "1 1 == 2").
 * @returns {boolean} True if sum of numeric tokens equals the last token.
 */
export const evaluateExpressionOld = expression => {
	let tokens = expression.split(/\W+/).map(exp => parseInt(exp));
	let match = tokens.pop();
	return sum(tokens) == match;
};
